import React, { useEffect, useRef, useState } from 'react';
import { Loader2, ClipboardPaste, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import CodeRing from './CodeRing';
import CodeBoxes from './CodeBoxes';

const mask = (p) => (p.length > 6 ? `${p.slice(0, 3)} ••••• ${p.slice(-3)}` : p);

/**
 * verify(code) → resolves to a truthy result when correct, falsy when not.
 * onVerified(result) runs after the green success animation.
 */
export default function PhoneCodeVerifier({ phone, channel = 'phone', verify, onVerified, onResend, onBack }) {
  const [digits, setDigits] = useState(Array(6).fill(''));
  const [status, setStatus] = useState('idle');
  const [cooldown, setCooldown] = useState(30);
  const refs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // After a wrong code, clear the boxes after 5s so a new code can be typed.
  useEffect(() => {
    if (status !== 'error') return;
    const t = setTimeout(() => {
      setDigits(Array(6).fill(''));
      setStatus('idle');
      refs.current[0]?.focus();
    }, 5000);
    return () => clearTimeout(t);
  }, [status]);

  const submit = async (value) => {
    setStatus('checking');
    const result = await verify(value).catch(() => null);
    if (!result) { setStatus('error'); return; }
    setStatus('success');
    setTimeout(() => onVerified(result), 1100);
  };

  const apply = (arr, focusIdx) => {
    setDigits(arr);
    if (status === 'error') setStatus('idle');
    if (focusIdx !== undefined) refs.current[Math.min(focusIdx, 5)]?.focus();
    const v = arr.join('');
    if (v.length === 6) submit(v);
  };

  const fillFrom = (start, str) => {
    const arr = [...digits];
    const chars = str.replace(/\D/g, '').slice(0, 6 - start).split('');
    chars.forEach((c, k) => { arr[start + k] = c; });
    apply(arr, start + chars.length);
  };

  const handleChange = (i, val) => {
    const clean = val.replace(/\D/g, '');
    if (clean.length > 1) return fillFrom(i, clean);
    const arr = [...digits];
    arr[i] = clean;
    apply(arr, clean ? i + 1 : undefined);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const arr = [...digits]; arr[i - 1] = '';
      apply(arr, i - 1);
    }
  };

  const paste = () => navigator.clipboard?.readText()
    .then((t) => fillFrom(0, t))
    .catch(() => toast.error('Could not read your clipboard — please type the code.'));

  const resend = async () => {
    await onResend();
    toast.success('New code sent!');
    setDigits(Array(6).fill('')); setStatus('idle'); setCooldown(30);
    refs.current[0]?.focus();
  };

  const isEmail = channel === 'email';
  const shown = isEmail ? phone : mask(phone);
  const filled = digits.filter(Boolean).length;
  const success = status === 'success';
  const hint = {
    idle: 'Type or paste the code. It checks itself after the 6th digit.',
    checking: 'Checking your code…',
    error: "That code doesn't match. Check the message and try again.",
    success: 'Continuing…',
  }[status];

  return (
    <div className="animate-fade-up">
      <CodeRing filled={filled} status={status} channel={channel} />
      <h1 className="text-2xl font-bold text-gray-900 text-center mt-5 mb-1">{success ? (isEmail ? 'Email verified' : 'Number verified') : (isEmail ? 'Check your email' : 'Check your phone')}</h1>
      <p className="text-gray-500 text-center text-sm mb-6 break-words">
        {success ? <><span className="font-semibold text-gray-800">{shown}</span> is confirmed on your account.</>
          : <>{isEmail ? 'We emailed a 6-digit code to' : 'We texted a 6-digit code to'} <span className="font-semibold text-gray-800">{shown}</span>.</>}
      </p>
      <div className="border-t-2 border-dashed border-gray-200 mb-6" />
      <CodeBoxes digits={digits} status={status} refs={refs} onChange={handleChange} onKeyDown={handleKeyDown} />
      <p className={`text-center text-sm mt-3 mb-5 min-h-[40px] ${status === 'error' ? 'text-red-600' : success ? 'text-brand-navy-deep font-semibold' : 'text-gray-500'}`}>{hint}</p>
      <button
        onClick={() => submit(digits.join(''))}
        disabled={filled < 6 || status !== 'idle'}
        className={`mx-auto h-12 text-base font-semibold flex items-center justify-center gap-2 transition-all duration-500 ease-out ${
          success || status === 'checking' ? 'w-12 rounded-full bg-brand-navy-deep text-white' : `w-full rounded-full ${filled === 6 && status !== 'error' ? 'bg-brand-navy-deep text-white' : 'bg-gray-100 text-gray-400'}`}`}
      >
        {status === 'checking' ? <Loader2 className="w-5 h-5 animate-spin" /> : success ? <Check className="w-6 h-6 animate-pop" strokeWidth={3} /> : 'Verify code'}
      </button>
      {!success && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button onClick={paste} className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900"><ClipboardPaste className="w-4 h-4" />Paste code</button>
          <div className="text-gray-500 text-right">
            Didn't get it?{' '}
            {cooldown > 0
              ? <span className="font-semibold text-gray-800">Resend in 0:{String(cooldown).padStart(2, '0')}</span>
              : <button onClick={resend} className="font-semibold text-blue-600 hover:underline">Resend</button>}
            {cooldown > 0 && <div className="h-0.5 bg-gray-200 rounded mt-1 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-1000 ease-linear" style={{ width: `${((30 - cooldown) / 30) * 100}%` }} /></div>}
          </div>
        </div>
      )}
      {!success && onBack && (
        <button onClick={onBack} className="w-full text-center text-gray-500 text-sm hover:text-gray-700 flex items-center justify-center gap-1 mt-4">
          <ArrowLeft className="w-4 h-4" /> {isEmail ? 'Change email' : 'Change number'}
        </button>
      )}
    </div>
  );
}